import { Tool, QuestionnaireData } from '../../types';
import { logger } from '../logger';

// Define categories that should be excluded based on team size
const TEAM_SIZE_EXCLUSIONS: Record<string, readonly string[]> = {
  'solo': ['HR & Payroll', 'Team Communication'],
  'small': ['HR & Payroll']
};

// Define development-related categories
const DEVELOPMENT_CATEGORIES = [
  'Development',
  'DevOps',
  'Version Control',
  'Engineering'
] as const;

export const ESSENTIAL_CATEGORIES: Record<string, string[]> = {
  'trades': [
    'Field Service',
    'Scheduling',
    'Payment Processing',
    'Accounting'
  ],
  'e-commerce': [
    'E-commerce',
    'Payment Processing',
    'Analytics',
    'Marketing'
  ],
  'agency': [
    'Project Management',
    'Time Tracking',
    'Accounting',
    'Collaboration'
  ],
  'startup': [
    'Project Management',
    'Analytics',
    'Customer Support',
    'DevOps'
  ],
  'professional-services': [
    'Scheduling',
    'Document Management',
    'Time Tracking',
    'Accounting'
  ],
  'healthcare': [
    'Scheduling',
    'Document Management',
    'Communication',
    'Accounting'
  ],
  'small-business': [
    'Accounting',
    'Project Management',
    'Payment Processing',
    'Document Management'
  ],
  'freelancer': [
    'Time Tracking',
    'Project Management',
    'Freelance Management',
    'Scheduling'
  ],
  'solopreneur': [
    'Project Management',
    'Accounting',
    'Time Tracking',
    'Marketing'
  ],
  'side-hustler': [
    'Marketing',
    'E-commerce',
    'Payment Processing',
    'Social Media Management'
  ],
  'content-creator': [
    'Social Media Management',
    'Marketing',
    'Design & Creative',
    'Project Management'
  ]
};

// Define categories that should be excluded for specific industries
const INDUSTRY_EXCLUSIONS: Record<string, string[]> = {
  'trades': ['Development', 'DevOps', 'Version Control', 'Engineering'],
  'retail': ['Development', 'DevOps', 'Version Control', 'Engineering'],
  'healthcare': ['Development', 'DevOps'],
  'home-services': ['Development', 'DevOps', 'Version Control', 'Engineering']
};

/**
 * Determines if a tool should be excluded based on business rules
 */
export function shouldExcludeTool(tool: Tool, data: QuestionnaireData): boolean {
  try {
    if (data.businessSubType && tool.notSuitableFor?.includes(data.businessSubType)) {
      logger.debug('Excluding tool not suitable for business subtype', {
        tool: tool.name,
        subType: data.businessSubType
      });
      return true;
    }

    // 1. Team Size Based Exclusions
    if (data.teamSize && TEAM_SIZE_EXCLUSIONS[data.teamSize]?.includes(tool.category)) {
      logger.debug('Excluding tool based on team size', {
        tool: tool.name,
        category: tool.category,
        teamSize: data.teamSize
      });
      return true;
    }

    // 2. Essential Tools Priority - HANDLED BY SCORING
    // NOTE: We no longer hard-exclude optional tools here. The scoring system handles
    // prioritization via ESSENTIAL_CATEGORY weight bonus. This fixes the bug where
    // user features like 'automation' were incorrectly compared against category
    // names like 'CRM', causing all optional tools to be excluded.

    // 3. Marketing Tool Exclusions - removed hard exclusion
    // Marketing tools are now penalized via scoring instead of blocked entirely.
    // This allows marketing tools to still appear if they match on other criteria.

    if (DEVELOPMENT_CATEGORIES.includes(tool.category as any)) {
      const isTechBusiness = data.industry === 'technology' || data.industry === 'creative';
      const isTechSubtype = data.businessSubType === 'saas' || data.businessSubType === 'mobile-app' || data.businessSubType === 'desktop-software';
      const isTechType = data.businessType === 'startup' || data.businessSubType === 'agency';

      if (!isTechBusiness && !isTechSubtype && !isTechType) {
        return true;
      }
    }

    // 5. Industry-Specific Exclusions
    if (data.industry && INDUSTRY_EXCLUSIONS[data.industry]?.includes(tool.category)) {
      logger.debug('Excluding tool based on industry', {
        tool: tool.name,
        category: tool.category,
        industry: data.industry
      });
      return true;
    }

    // 6. Budget-Based Exclusions
    // Only exclude tools that are strictly above the user's budget tier
    // Tools with multiple pricing tiers (e.g., ['free', 'medium']) are included
    // if any tier matches the user's budget or below
    if (data.budget && tool.pricing && tool.pricing.length > 0) {
      const budgetTiers = ['free', 'low', 'medium', 'high'] as const;
      const userBudgetIndex = budgetTiers.indexOf(data.budget);

      // Check if tool has ANY pricing option at or below user's budget
      const hasAffordableOption = tool.pricing.some(price => {
        const priceIndex = budgetTiers.indexOf(price);
        return priceIndex !== -1 && priceIndex <= userBudgetIndex;
      });

      if (!hasAffordableOption) {
        logger.debug('Excluding tool above budget', {
          tool: tool.name,
          pricing: tool.pricing,
          userBudget: data.budget
        });
        return true;
      }
    }

    // 7. Technical Expertise Exclusions
    // Only exclude tools that require advanced expertise for beginners.
    // Intermediate tools are kept but penalized in scoring.
    if (data.technicalExpertise === 'beginner' &&
        tool.technicalLevel?.every(level => level === 'advanced')) {
      logger.debug('Excluding advanced-only tool for beginner', {
        tool: tool.name,
        requiredLevel: tool.technicalLevel
      });
      return true;
    }

    return false;
  } catch (error) {
    logger.error('Error in shouldExcludeTool:', error);
    return false; // Don't exclude on error to avoid filtering too aggressively
  }
}